import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Mail,
  Lock,
  Eye,
  EyeOff,
  ArrowRight,
  Building2,
  User,
  Truck,
  Store,
  UtensilsCrossed,
  Briefcase,
  type LucideIcon,
} from 'lucide-react';
import { registerErrorMessage, useRegister } from './use-auth';
import { AuthShowcase } from './AuthShowcase';
import { BrandLogo } from '@/shared/brand/BrandLogo';

interface Rubro {
  key: string;
  name: string;
  description: string;
  Icon: LucideIcon;
}

// Espejo de RUBROS en apps/api/src/modules/platform/module-registry.ts.
const RUBROS: Rubro[] = [
  { key: 'distribuidora', name: 'Distribuidora / Frutas', description: 'Compra y reventa con control de stock.', Icon: Truck },
  { key: 'retail', name: 'Retail / Ferretería', description: 'Venta al público con inventario.', Icon: Store },
  { key: 'restaurante', name: 'Restaurante / Bar', description: 'Punto de venta ágil con insumos.', Icon: UtensilsCrossed },
  { key: 'servicios', name: 'Servicios', description: 'Facturación sin manejo de inventario.', Icon: Briefcase },
];

export function RegisterPage(): JSX.Element {
  const register = useRegister();
  const [ruc, setRuc] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [tradeName, setTradeName] = useState('');
  // La landing enlaza con `?rubro=<key>` para llegar con el giro ya elegido.
  const [params] = useSearchParams();
  const [rubro, setRubro] = useState<string>(() => {
    const wanted = params.get('rubro');
    return RUBROS.some((r) => r.key === wanted) ? wanted! : 'distribuidora';
  });
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);

  const submit = (e: FormEvent): void => {
    e.preventDefault();
    register.mutate({
      ruc,
      businessName,
      tradeName: tradeName.trim() || undefined,
      rubro,
      owner: { name, email, password },
    });
  };

  return (
    <div
      className="cx"
      style={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        padding: 24,
        background:
          'radial-gradient(900px 500px at 12% 0%, rgba(120,210,240,.20), transparent 60%), radial-gradient(800px 480px at 100% 100%, rgba(171,239,255,.18), transparent 60%), #EEF1F6',
      }}
    >
      <div
        className="card fade-in login-card"
        style={{ width: 1060, maxWidth: '100%', overflow: 'hidden', boxShadow: 'var(--sh-lg)', borderRadius: 24, minHeight: 660 }}
      >
        {/* LEFT — form */}
        <form onSubmit={submit} style={{ padding: '34px 48px 28px', display: 'flex', flexDirection: 'column', minWidth: 0, overflowY: 'auto', maxHeight: '90vh' }}>
          <div className="row between aic">
            <BrandLogo variant="wordmark-dark" height={40} />
            <div className="t-sm muted">
              ¿Ya tienes cuenta?{' '}
              <Link to="/login" style={{ color: 'var(--ink)', fontWeight: 700, textDecoration: 'underline', textUnderlineOffset: 3 }}>
                Inicia sesión
              </Link>
            </div>
          </div>

          <div style={{ padding: '20px 0' }}>
            <h1 style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-.02em', margin: '0 0 6px' }}>Crea tu cuenta</h1>
            <p className="muted t-sm" style={{ margin: '0 0 22px' }}>
              Empieza tu prueba gratuita de Cajita.
            </p>

            <div className="col gap14">
              {/* Empresa */}
              <div className="field">
                <label className="label">RUC</label>
                <div className="input-icon">
                  <Building2 size={17} />
                  <input
                    className="input"
                    inputMode="numeric"
                    required
                    minLength={11}
                    value={ruc}
                    onChange={(e) => setRuc(e.target.value.replace(/\D/g, '').slice(0, 11))}
                    placeholder="20123456789"
                  />
                </div>
              </div>
              <div className="field">
                <label className="label">Razón social</label>
                <input className="input" required value={businessName} onChange={(e) => setBusinessName(e.target.value)} placeholder="Mi Empresa S.A.C." />
              </div>
              <div className="field">
                <label className="label">
                  Nombre comercial <span className="muted2">(opcional)</span>
                </label>
                <input className="input" value={tradeName} onChange={(e) => setTradeName(e.target.value)} placeholder="Cajita" />
              </div>

              {/* Rubro */}
              <div className="field">
                <label className="label">¿A qué se dedica tu negocio?</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 4 }}>
                  {RUBROS.map((r) => {
                    const active = rubro === r.key;
                    return (
                      <button
                        key={r.key}
                        type="button"
                        onClick={() => setRubro(r.key)}
                        style={{
                          textAlign: 'left',
                          display: 'flex',
                          gap: 10,
                          alignItems: 'flex-start',
                          padding: '12px 13px',
                          borderRadius: 12,
                          cursor: 'pointer',
                          background: active ? 'var(--primary-soft)' : 'var(--surface, #fff)',
                          border: `1.5px solid ${active ? 'var(--primary)' : 'var(--border)'}`,
                          transition: 'border-color .15s, background .15s',
                        }}
                        aria-pressed={active}
                      >
                        <div
                          style={{
                            width: 32,
                            height: 32,
                            borderRadius: 9,
                            flex: 'none',
                            display: 'grid',
                            placeItems: 'center',
                            background: active ? 'var(--primary)' : 'var(--canvas)',
                            color: active ? 'var(--primary-ink)' : 'var(--ink-2)',
                          }}
                        >
                          <r.Icon size={17} />
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div className="fw6 t-sm">{r.name}</div>
                          <div className="t-xs muted">{r.description}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Tu cuenta */}
              <div className="field">
                <label className="label">Tu nombre</label>
                <div className="input-icon">
                  <User size={17} />
                  <input className="input" required value={name} onChange={(e) => setName(e.target.value)} placeholder="Jorge Pérez" />
                </div>
              </div>
              <div className="field">
                <label className="label">Correo electrónico</label>
                <div className="input-icon">
                  <Mail size={17} />
                  <input className="input" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="nombre@empresa.pe" />
                </div>
              </div>
              <div className="field">
                <label className="label">Contraseña</label>
                <div className="input-icon">
                  <Lock size={17} />
                  <input
                    className="input"
                    type={show ? 'text' : 'password'}
                    autoComplete="new-password"
                    required
                    minLength={8}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Mínimo 8 caracteres"
                    style={{ paddingRight: 40 }}
                  />
                  <button
                    type="button"
                    onClick={() => setShow((p) => !p)}
                    style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'transparent', color: 'var(--muted-2)' }}
                    aria-label={show ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  >
                    {show ? <EyeOff size={17} /> : <Eye size={17} />}
                  </button>
                </div>
              </div>

              {register.isError && (
                <div className="t-sm" style={{ color: 'var(--error)' }}>
                  {registerErrorMessage(register.error)}
                </div>
              )}
              <button className="btn btn-primary btn-lg btn-block" type="submit" disabled={register.isPending} style={{ marginTop: 4 }}>
                {register.isPending ? 'Creando cuenta…' : 'Crear cuenta'}
                <ArrowRight size={18} />
              </button>
              <p className="t-xs muted2" style={{ textAlign: 'center', margin: 0 }}>
                Al registrarte aceptas los términos y la política de privacidad.
              </p>
            </div>
          </div>
        </form>

        {/* RIGHT — showcase */}
        <AuthShowcase />
      </div>
    </div>
  );
}
